import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import { authService } from '@/services/auth'
import type { User, LoginRequest, RegisterRequest } from '@/types'

// =============================================================================
// Auth Context Types
// =============================================================================

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginRequest) => Promise<void>
  register: (data: RegisterRequest) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
}

// =============================================================================
// Auth Context
// =============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// =============================================================================
// Auth Provider
// =============================================================================

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Schedule a proactive token refresh before it expires
  const scheduleTokenRefresh = useCallback(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }

    const token = sessionStorage.getItem('access_token')
    if (!token) return

    // Decode JWT payload to get expiration time
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const expiresAt = payload.exp * 1000 // Convert to ms
      // Refresh 5 minutes before expiry
      const refreshAt = expiresAt - 5 * 60 * 1000
      const delay = refreshAt - Date.now()

      if (delay <= 0) {
        // Token already close to expiring or expired — refresh now
        authService.refreshToken()
          .then(() => scheduleTokenRefresh())
          .catch(() => { /* interceptor will handle it */ })
        return
      }

      refreshTimerRef.current = setTimeout(async () => {
        try {
          await authService.refreshToken()
          scheduleTokenRefresh() // Schedule next refresh
        } catch {
          // Refresh failed — interceptor will handle 401 on next request
        }
      }, delay)
    } catch {
      // Can't decode token — ignore, interceptor will handle on next request
    }
  }, [])

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
      }
    }
  }, [])

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      const storedUser = authService.getStoredUser()
      const token = authService.getToken()

      if (token && storedUser) {
        setUser(storedUser)
        // Optionally verify token is still valid
        try {
          const freshUser = await authService.getCurrentUser()
          setUser(freshUser)
          sessionStorage.setItem('user', JSON.stringify(freshUser))
          scheduleTokenRefresh()
        } catch {
          // Token invalid, clear auth
          authService.logout()
          setUser(null)
        }
      }
      setIsLoading(false)
    }

    initAuth()
  }, [scheduleTokenRefresh])

  const login = useCallback(async (credentials: LoginRequest) => {
    const { user } = await authService.login(credentials)
    setUser(user)
    scheduleTokenRefresh()
  }, [scheduleTokenRefresh])

  const register = useCallback(async (data: RegisterRequest) => {
    await authService.register(data)
  }, [])

  const logout = useCallback(() => {
    authService.logout()
    setUser(null)
  }, [])

  const refreshUser = useCallback(async () => {
    try {
      const freshUser = await authService.getCurrentUser()
      setUser(freshUser)
      sessionStorage.setItem('user', JSON.stringify(freshUser))
    } catch {
      // Token invalid
      logout()
    }
  }, [logout])

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// =============================================================================
// useAuth Hook
// =============================================================================

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
